// A tooltip on hover (desktop) or tap (phone), 29. 9. 2026: the locked menu entries (`Unlocks at level
// 5`), the header's energy, the locked Trader row. The tip may hold a link (`Learn more`); it stays open
// while the pointer is on the trigger or the tip, and a tap outside closes it.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { t } from '@/i18n';
import { cx } from '@/lib/format';

export function Tip({
  tip,
  children,
  className,
  triggerClassName,
  align = 'left',
  label,
  testId,
  triggerTestId,
  locked,
  onMouseClick,
  fit,
}: {
  tip: ReactNode;
  children: ReactNode;
  className?: string;
  triggerClassName?: string;
  align?: 'left' | 'right';
  label?: string;
  testId?: string;
  triggerTestId?: string;
  /** A locked entry (`data-locked`; its label says locked): a tap only shows the tip. */
  locked?: boolean;
  /** A mouse click does this instead (the tip is on hover); a tap still shows the tip. */
  onMouseClick?: () => void;
  /** 13. 10. 2026: the tip as wide as its trigger, wrapping (the side menu, which must not scroll sideways). */
  fit?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const hover = useRef(false);
  const pointer = useRef<string>('');
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  return (
    <span
      ref={box}
      className={cx('relative', className)}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') {
          hover.current = true;
          setOpen(true);
        }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') {
          hover.current = false;
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-label={label}
        data-testid={triggerTestId}
        data-locked={locked || undefined}
        onPointerDown={(e) => {
          pointer.current = e.pointerType;
        }}
        onClick={() => {
          if (onMouseClick && pointer.current === 'mouse') {
            onMouseClick();
            return;
          }
          // A click right after the mouse opened it keeps it open; a tap toggles it.
          const keep = hover.current;
          hover.current = false;
          setOpen((o) => keep || !o);
        }}
        className={triggerClassName}
      >
        {children}
      </button>
      {open && (
        // The padding bridges the gap, so the pointer can reach a link in the tip.
        <span className={cx('absolute top-full z-50 pt-1.5', fit ? 'inset-x-0' : align === 'right' ? 'right-0' : 'left-0')}>
          <span
            role="tooltip"
            data-testid={testId}
            className={cx('block rounded-[6px] border border-border bg-raised px-3 py-2 text-left text-[13px] font-normal normal-case leading-[18px] tracking-normal text-text shadow-pop', fit ? 'w-full' : 'w-max max-w-[min(300px,calc(100vw-24px))]')}
          >
            {tip}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * `Learn more` in a tip or next to a control: the Guide section (29. 9. 2026). `newTab` where leaving
 * the screen would lose something (the encounter card and its countdown).
 */
export function LearnMore({ section, className, newTab }: { section: string; className?: string; newTab?: boolean }) {
  const cls = cx('whitespace-nowrap font-semibold !text-text underline decoration-line underline-offset-4', className);
  const to = `/guide?from=learn#${section}`;
  return newTab ? (
    <a href={to} target="_blank" rel="noopener" data-testid={`learn-more-${section}`} className={cls}>
      {t('common.learnMore')}
    </a>
  ) : (
    <Link to={to} data-testid={`learn-more-${section}`} className={cls}>
      {t('common.learnMore')}
    </Link>
  );
}
